import React, { useState, useRef } from "react";
import {
  TextField,
  Button,
  Autocomplete,
  Box,
  IconButton,
  Typography,
  Grid,
  FormControl,
  FormLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import styled from "styled-components";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const StyledTextField = styled(TextField)(({ status }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#D5D7DA",
      borderRadius: "10px",
    },
  },
}));

const ComplaintForm = ({ handleClose }) => {
  const [formValues, setFormValues] = useState({
    complaintName: "",
    email: "",
    contactNo: "",
    subject: "",
    category: "",
    description: "",
  });
  const [attachment, setAttachment] = useState(null);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const fileInputRef = useRef(null);

  const handleBlur = (field) => {
    if (!formValues[field]) {
      setErrors((prev) => ({ ...prev, [field]: "This field is required." }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(formValues).forEach((key) => {
      if (!formValues[key]) {
        newErrors[key] = "This field is required.";
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      try {
        const formData = new FormData();
        Object.keys(formValues).forEach((key) => {
          formData.append(key, typeof formValues[key] === "object" ? formValues[key]?.value || formValues[key] : formValues[key]);
        });
        if (attachment) {
          formData.append("attachment", attachment);
        }
        await postData("student/complaint/create", formData);
        setToast({ open: true, severity: "success", message: "Complaint submitted successfully" });
        handleClose();
      } catch (error) {
        setToast({ open: true, severity: "error", message: "Failed to submit complaint" });
      }
    }
  };

  const formClose = () => {
    handleClose();
    setErrors({});
  };

  const categoryOptions = [
    { label: "Academic", value: "Academic" },
    { label: "Facility", value: "Facility" },
    { label: "Transport", value: "Transport" },
    { label: "Teacher", value: "Teacher" },
    { label: "Other", value: "Other" },
  ];

  const styles = {
    formContainer: { padding: "20px" },
    label: { color: "#8F95B2" },
    buttonContainer: { display: "flex", justifyContent: "center", gap: 5, mt: 3, mb: 2 },
    cancelButton: {
      color: "#414651",
      fontSize: "16px",
      textTransform: "none",
      padding: "5px 40px",
      borderRadius: "10px",
      border: "1px solid #D5D7DA",
    },
    submitButton: {
      backgroundColor: "#336599",
      color: "#FFFFFF",
      fontSize: "16px",
      textTransform: "none",
      padding: "5px 40px",
      borderRadius: "10px",
    },
    dropZone: {
      border: "2px dashed #D5D7DA",
      borderRadius: "10px",
      padding: "24px",
      textAlign: "center",
      cursor: "pointer",
      backgroundColor: "#fafafa",
      transition: "border-color 0.3s",
      "&:hover": { borderColor: "#244C77" },
    },
  };

  return (
    <Box component="form" sx={styles.formContainer} onSubmit={handleSubmit}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4 style={{ margin: 0 }}>Add Complaint</h4>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Grid container spacing={2} px={3}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Complaint Name</FormLabel>
            <StyledTextField
              size="small"
              name="complaintName"
              value={formValues.complaintName}
              onChange={handleChange}
              onBlur={() => handleBlur("complaintName")}
              error={!!errors.complaintName}
              helperText={errors.complaintName}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Email Id</FormLabel>
            <StyledTextField
              size="small"
              name="email"
              type="email"
              value={formValues.email}
              onChange={handleChange}
              onBlur={() => handleBlur("email")}
              error={!!errors.email}
              helperText={errors.email}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Contact No</FormLabel>
            <StyledTextField
              size="small"
              name="contactNo"
              value={formValues.contactNo}
              onChange={handleChange}
              onBlur={() => handleBlur("contactNo")}
              error={!!errors.contactNo}
              helperText={errors.contactNo}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Subject</FormLabel>
            <StyledTextField
              size="small"
              name="subject"
              value={formValues.subject}
              onChange={handleChange}
              onBlur={() => handleBlur("subject")}
              error={!!errors.subject}
              helperText={errors.subject}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              options={categoryOptions}
              getOptionLabel={(option) => option.label}
              value={formValues.category || null}
              onChange={(event, newValue) =>
                handleChange({ target: { name: "category", value: newValue } })
              }
              onBlur={() => handleBlur("category")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Category"
                  error={!!errors.category}
                  helperText={errors.category}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Description</FormLabel>
            <StyledTextField
              size="small"
              name="description"
              multiline
              rows={3}
              value={formValues.description}
              onChange={handleChange}
              onBlur={() => handleBlur("description")}
              error={!!errors.description}
              helperText={errors.description}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={styles.dropZone}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".svg,.png,.jpg,.gif"
              onChange={handleFileChange}
            />
            <CloudUploadOutlinedIcon sx={{ fontSize: "40px", color: "#8F95B2" }} />
            <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 1 }}>
              {attachment ? attachment.name : "Click to upload or drag and drop"}
            </Typography>
            <Typography sx={{ color: "#bbb", fontSize: "11px" }}>
              SVG, PNG, JPG or GIF (max. 800x400px)
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Box sx={styles.buttonContainer}>
        <Button onClick={formClose} sx={styles.cancelButton}>Cancel</Button>
        <Button type="submit" sx={styles.submitButton}>Submit</Button>
      </Box>
    </Box>
  );
};

export default ComplaintForm;
